import {
    ColumnPropertyHandler,
    ColumnPropertyFlag,
    SwitchPropertyHandler,
    EnumPropertyHandler,
} from "./property-handlers";
import {
    getPrimitiveColumns,
    ComponentKind,
    PropertyDescription,
    ComponentDescription,
    TableColumn,
    InputOutputTables,
    MutatingScreenKind,
    TableGlideType,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { SerializedComponent } from "../../components/SerializedApp";
import {
    ComponentDescriptor,
    PropertyDescriptor,
    ComponentBuilder,
    InteractiveComponentConfiguratorContext,
    ComponentConfiguratorContext,
    RequiredKind,
} from ".";
import { makeNumberProperty } from "./property-utils";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import {
    makeNumberPropertyDescriptor,
    makeCaptionStringPropertyDescriptor,
    makeTextPropertyDescriptor,
} from "./descriptor-utils";
import { ProgressType } from "./progress-types";
import { getPropertyWithColumnFormat } from "../formulas";
import { makeNumber, makeText } from "../builder-utils";

const ComponentKindProgress: ComponentKind = "progress";

const valuePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.DefaultCaption],
    undefined,
    [],
    getPrimitiveColumns,
    "number",
    PropertySection.Data
);

const showTotalInCenterPropertyHandler = new SwitchPropertyHandler(
    { showTotalInCenter: true },
    "Show total in center",
    PropertySection.Design
);

const stylePropertyHandler = new EnumPropertyHandler(
    { style: ProgressType.BarProgress },
    "Style",
    "Style",
    [
        {
            value: ProgressType.BarProgress,
            label: "Bar",
            icon: "progressBar",
        },
        {
            value: ProgressType.CircleProgress,
            label: "Circle",
            icon: "donutChartReverse",
        },
        { value: ProgressType.HalfCircleProgress, label: "Half Circle", icon: "halfCircleProgressBar" },
    ],

    PropertySection.Style,
    "large-images"
);

interface ProgressComponentDescription extends ComponentDescription {
    readonly valueProperty: PropertyDescription;
    readonly minimumProperty: PropertyDescription;
    readonly maximumProperty: PropertyDescription;
    readonly subtitle: PropertyDescription | undefined;
    readonly caption: PropertyDescription | undefined;
    readonly showTotalInCenter: PropertyDescription;
    readonly showLegendByDefault: PropertyDescription;
    readonly style: PropertyDescription;
}
export class ProgressComponentHandler extends ComponentHandlerBase<ProgressComponentDescription> {
    constructor() {
        super(ComponentKindProgress);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ProgressComponentDescription,
        { input }: InputOutputTables
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = valuePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const showTotal = b.constant(showTotalInCenterPropertyHandler.getSwitch(desc));

        const style = stylePropertyHandler.getEnum(desc);

        const formatted = getPropertyWithColumnFormat(builder, input, propertyName, b.getContext);
        if (style === ProgressType.BarProgress) {
            return b.ui("ProgressIndicator", {
                value: b.getProperty(propertyName, b.getContext, input),
                formatted,
                minimum: makeNumber(builder, desc.minimumProperty, input),
                maximum: makeNumber(builder, desc.maximumProperty, input),
                title: makeText(builder, desc.caption, input, true),
                caption: makeText(builder, desc.subtitle, input, true),
                style: b.constant(style),
            });
        } else {
            return b.ui("CircleProgressIndicator", {
                value: b.getProperty(propertyName, b.getContext, input),
                formatted,
                minimum: makeNumber(builder, desc.minimumProperty, input),
                maximum: makeNumber(builder, desc.maximumProperty, input),
                title: makeText(builder, desc.caption, input, true),
                caption: makeText(builder, desc.subtitle, input, true),
                showTotal,
                style: b.constant(style),
            });
        }
    }

    public getDescriptor(
        desc: ProgressComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ProgressComponentDescription> {
        const props: PropertyDescriptor[] = [
            valuePropertyHandler,
            makeNumberPropertyDescriptor(
                "minimumProperty",
                "Minimum",
                "0",
                RequiredKind.Required,
                0,
                mutatingScreenKind,
                {
                    preferredNames: ["minimum", "min", "low"],
                }
            ),
            makeNumberPropertyDescriptor(
                "maximumProperty",
                "Maximum",
                "100",
                RequiredKind.Required,
                100,
                mutatingScreenKind,
                {
                    preferredNames: ["maximum", "max", "high"],
                }
            ),
            makeCaptionStringPropertyDescriptor("Progress", false, mutatingScreenKind),
            makeTextPropertyDescriptor(
                "subtitle",
                "Caption",
                "Enter the component's caption",
                false,
                mutatingScreenKind,
                {
                    propertySection: PropertySection.Design,
                    emptyByDefault: true,
                }
            ),
            stylePropertyHandler,

            ...this.getBasePropertyDescriptors(),
        ];

        if (desc !== undefined) {
            const style = stylePropertyHandler.getEnum(desc);

            if (style !== ProgressType.BarProgress) {
                props.push(showTotalInCenterPropertyHandler);
            }
        }
        return {
            name: "Progress Bar",
            description: "A bar showing progress",
            img: "componentProgressBar",
            group: "Charts",
            helpUrl: getDocURL("progress"),
            properties: props,
        };
    }

    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ProgressComponentDescription | undefined {
        const desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc === undefined) return undefined;

        return {
            ...desc,
            minimumProperty: makeNumberProperty(0),
            maximumProperty: makeNumberProperty(100),
        };
    }
}
